import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, setStoreIdHeader, setUserHeader } from "./api";

import RegisterUsers from "./RegisterUsers";
import UsersList from "./UsersList";
import BusinessList from "./BusinessList";
import InventoryList from "./InventoryList";
import SalesWindow from "./SalesWindow";
import Reports from "./Reports";
import SalesSummaryReport from "./SalesSummaryReport";
import ReplenishReport from "./ReplenishReport";
import POSUI from "./POSUI";
import SaleItemsReport from "./SaleItemsReport";
import ReplenishmentReport from "./ReplenishmentReport";
import StockReport from "./StockReport";
import RegisterStore from "./RegisterStore";
import UsersManager from "./UsersManager";

import HotelRoomsScreen from "./HotelRoomsScreen";
import * as HotelGuestsScreenMod from "./HotelGuestsScreen";
import * as HotelStaysScreenMod from "./HotelStaysScreen";
import * as HotelCheckInScreenMod from "./HotelCheckInScreen";
import HotelSuppliesPOS from "./HotelSuppliesPOS";
import HotelReportsScreen from "./HotelReportsScreen";
import HotelSuppliesReplenish from "./HotelSuppliesReplenish";

function safeParseJSON(v) {
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
}

function pickComponent(mod, named) {
  const c = mod?.default || (named ? mod?.[named] : null);
  return typeof c === "function" ? c : null;
}

export default function Dashboard() {
  const navigate = useNavigate();

  const HotelGuestsScreen = pickComponent(
    HotelGuestsScreenMod,
    "HotelGuestsScreen"
  );
  const HotelStaysScreen = pickComponent(
    HotelStaysScreenMod,
    "HotelStaysScreen"
  );
  const HotelCheckInScreen = pickComponent(
    HotelCheckInScreenMod,
    "HotelCheckInScreen"
  );

  const [page, setPage] = useState("home");
  const [navOpen, setNavOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [showTiles, setShowTiles] = useState(true);

  const [storeId, setStoreId] = useState(() =>
    Number(localStorage.getItem("store_id") || 0)
  );
  const [storeType, setStoreType] = useState(() =>
    (localStorage.getItem("store_type") || "retail").toLowerCase()
  );

  const fileBtnRef = useRef(null);
  const fileMenuRef = useRef(null);
  const reportsBtnRef = useRef(null);
  const reportsMenuRef = useRef(null);

  const prevStoreIdRef = useRef(0);

  useEffect(() => {
    localStorage.setItem("theme", "light");
    document.documentElement.classList.remove("dark");
  }, []);

  const [rawSession, setRawSession] = useState(() =>
    safeParseJSON(localStorage.getItem("user") || "null")
  );

  useEffect(() => {
    const sync = () =>
      setRawSession(safeParseJSON(localStorage.getItem("user") || "null"));
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const sessionUser = rawSession?.user || rawSession || null;
  const role = String(sessionUser?.role || "").toLowerCase();

  const permissions = useMemo(() => {
    const p = sessionUser?.permissions;
    return Array.isArray(p) ? p : [];
  }, [sessionUser]);

  const usernameFromSession =
    sessionUser?.username || localStorage.getItem("username") || "";

  const can = (perm) => {
    if (role === "super_user") return true;
    if (permissions.includes("*")) return true;
    return permissions.includes(perm);
  };

  const canBusiness = () =>
    role === "super_user" || permissions.includes("*") || role === "admin";

  const canRegisterStore = () =>
    role === "super_user" || permissions.includes("*");

  const canHotelView = () => can("hotel.view") || can("hotel.manage");
  const canHotelManage = () => can("hotel.manage");

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    const sid = Number(localStorage.getItem("store_id") || 0);

    if (!userStr || !sid) {
      navigate("/login", { replace: true });
      return;
    }

    const u = safeParseJSON(userStr);

    setStoreId(sid);
    setStoreIdHeader(sid);
    localStorage.setItem("store_id", String(sid));

    const username = u?.user?.username || u?.username || "";
    setUserHeader(username);
    if (username) localStorage.setItem("username", username);

    const st = (localStorage.getItem("store_type") || "retail").toLowerCase();
    setStoreType(st);
  }, [navigate]);

  useEffect(() => {
    if (page === "register-store") return;
    setStoreIdHeader(storeId);
    if (storeId > 0) localStorage.setItem("store_id", String(storeId));
  }, [storeId, page]);

  const openDefaultForType = (t) => {
    const type = (t || "").toLowerCase();

    if (type === "hotel" || type === "restaurant") {
      setPage("home");
      setShowTiles(true);
      return;
    }

    if (type === "bar") {
      if (can("pos.sell")) {
        setPage("pos");
        setShowTiles(false);
      } else {
        setPage("home");
        setShowTiles(true);
      }
      return;
    }

    if (can("pos.sell")) {
      setPage("sale");
      setShowTiles(false);
    } else {
      setPage("home");
      setShowTiles(true);
    }
  };

  useEffect(() => {
    openDefaultForType(storeType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (
        fileOpen &&
        fileMenuRef.current &&
        !fileMenuRef.current.contains(e.target) &&
        fileBtnRef.current &&
        !fileBtnRef.current.contains(e.target)
      ) {
        setFileOpen(false);
      }

      if (
        reportsOpen &&
        reportsMenuRef.current &&
        !reportsMenuRef.current.contains(e.target) &&
        reportsBtnRef.current &&
        !reportsBtnRef.current.contains(e.target)
      ) {
        setReportsOpen(false);
      }
    };

    window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [fileOpen, reportsOpen]);

  const go = (p) => {
    setPage(p);
    setNavOpen(false);
    setFileOpen(false);
    setReportsOpen(false);
  };

  const handleTileSelect = (cb) => {
    cb();
    setShowTiles(false);
  };

  const handleShowMenu = () => {
    if (page === "register-store" && prevStoreIdRef.current > 0) {
      setStoreId(prevStoreIdRef.current);
      setStoreIdHeader(prevStoreIdRef.current);
      localStorage.setItem("store_id", String(prevStoreIdRef.current));
    }

    setShowTiles(true);
    setPage("home");
    window.scrollTo?.(0, 0);
  };

  const focusFirstMenuItem = (menuRef) => {
    menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  };

  const onDropdownButtonKeyDown = (e, which) => {
    if (e.key === "Escape") {
      if (which === "file") setFileOpen(false);
      if (which === "reports") setReportsOpen(false);
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (which === "file") focusFirstMenuItem(fileMenuRef);
      if (which === "reports") focusFirstMenuItem(reportsMenuRef);
    }
  };

  const onMenuItemKeyDown = (e, which) => {
    const menuRef = which === "file" ? fileMenuRef : reportsMenuRef;
    const btnRef = which === "file" ? fileBtnRef : reportsBtnRef;

    const items = Array.from(
      menuRef.current?.querySelectorAll('[role="menuitem"]') || []
    );
    if (!items.length) return;

    const idx = items.indexOf(e.currentTarget);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    }
    if (e.key === "Escape") {
      if (which === "file") setFileOpen(false);
      if (which === "reports") setReportsOpen(false);
      btnRef.current?.focus();
    }
  };

  const type = (storeType || "retail").toLowerCase();
  const isBar = type === "bar";
  const isRetail = type === "retail";
  const isHotel = type === "hotel";
  const isRestaurant = type === "restaurant";

  const allowHotelModule = isHotel || isRestaurant;

  const doLogout = () => {
    const apiBase = localStorage.getItem("api_base");
    localStorage.clear();
    if (apiBase) localStorage.setItem("api_base", apiBase);

    localStorage.setItem("theme", "light");
    document.documentElement.classList.remove("dark");

    setStoreIdHeader(0);
    setUserHeader("");
    setStoreType("retail");
    setStoreId(0);

    navigate("/login", { replace: true });
  };

  const openRegisterStore = () => {
    if (!canRegisterStore()) return;

    prevStoreIdRef.current = Number(
      localStorage.getItem("store_id") || storeId || 0
    );

    setStoreId(0);
    setStoreIdHeader(0);

    const u = localStorage.getItem("username") || usernameFromSession || "";
    setUserHeader(u);

    go("register-store");
  };

  const actions = useMemo(() => {
    const a = [];

    if (allowHotelModule && canHotelView()) {
      a.push({
        key: "hotel-checkin",
        label: "Hotel Check-In / Out",
        icon: "🏨",
        onClick: () => go("hotel-checkin"),
      });
      a.push({
        key: "hotel-rooms",
        label: "Hotel Rooms",
        icon: "🚪",
        onClick: () => go("hotel-rooms"),
      });
      a.push({
        key: "hotel-guests",
        label: "Hotel Guests",
        icon: "🧑‍🤝‍🧑",
        onClick: () => go("hotel-guests"),
      });
      a.push({
        key: "hotel-stays",
        label: "Hotel Stays",
        icon: "🗓️",
        onClick: () => go("hotel-stays"),
      });
      a.push({
        key: "hotel-reports",
        label: "Hotel Reports",
        icon: "📊",
        onClick: () => go("hotel-reports"),
      });
      a.push({
        key: "hotel-supplies-pos",
        label: "Guest Supplies POS",
        icon: "🛍️",
        onClick: () => go("hotel-supplies-pos"),
      });
      a.push({
        key: "hotel-supplies-replenish",
        label: "Replenish Supplies",
        icon: "📦",
        onClick: () => go("hotel-supplies-replenish"),
      });
    }

    if (can("pos.sell")) {
      if (isRetail) {
        a.push({
          key: "sale",
          label: "New Sale",
          icon: "🛒",
          onClick: () => go("sale"),
        });
      }
      if (isBar) {
        a.push({
          key: "pos",
          label: "Point of Sale (Bar)",
          icon: "🍹",
          onClick: () => go("pos"),
        });
      }
    }

    if (can("inventory.view")) {
      a.push({
        key: "inventory",
        label: "Inventory",
        icon: "📦",
        onClick: () => go("inventory"),
      });
    }

    if (can("reports.view")) {
      a.push({
        key: "reports",
        label: "Sales Report",
        icon: "📈",
        onClick: () => go("reports"),
      });
      a.push({
        key: "sales-summary",
        label: "Sales Summary",
        icon: "👤📊",
        onClick: () => go("sales-summary"),
      });
      a.push({
        key: "sale-items-report",
        label: "Sales Items Report",
        icon: "🧾",
        onClick: () => go("sale-items-report"),
      });
      a.push({
        key: "replenishment-report",
        label: "Replenishment Report",
        icon: "➕",
        onClick: () => go("replenishment-report"),
      });
      a.push({
        key: "stock-report",
        label: "Stock Report",
        icon: "📦🧾",
        onClick: () => go("stock-report"),
      });
      a.push({
        key: "replenish-report",
        label: "Legacy Replenish Page",
        icon: "🧰",
        onClick: () => go("replenish-report"),
      });
    }

    if (can("users.manage")) {
      a.push({
        key: "users-manager",
        label: "User Management",
        icon: "👥",
        onClick: () => go("users-manager"),
      });
    }

    if (canBusiness()) {
      a.push({
        key: "business",
        label: "Business Registration",
        icon: "🏢",
        onClick: () => go("business"),
      });
    }

    if (canRegisterStore()) {
      a.push({
        key: "register-store",
        label: "Register Store",
        icon: "🏪",
        onClick: () => openRegisterStore(),
      });
    }

    a.push({
      key: "logout",
      label: "Logout",
      icon: "🚪",
      onClick: doLogout,
    });

    return a;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowHotelModule, isRetail, isBar, role, permissions.join("|")]);

  const Forbidden = ({ need }) => (
    <div className="notice">
      <b>Forbidden.</b> You don’t have access. Required: <code>{need}</code>
    </div>
  );

  const MissingHotel = ({ name }) => (
    <div className="notice">
      <b>Hotel module import issue:</b> <code>{name}</code> is not exported
      correctly. Export it as <code>export default</code> OR{" "}
      <code>export function {name}()</code>.
    </div>
  );

  const username = usernameFromSession || "";

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brandWrap">
          <div className="brandMark" aria-hidden="true">
            K
          </div>
          <div className="brandText">
            <div className="brandTitle">KevSoft POS</div>
            <div className="brandSub">
              Store #{storeId || "—"} · {type.toUpperCase()}
            </div>
          </div>
        </div>

        <button
          className="iconBtn hamburger"
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          ☰
        </button>

        <nav className={`nav ${navOpen ? "open" : ""}`}>
          <div className="dropdown">
            <button
              ref={fileBtnRef}
              className="navBtn"
              aria-haspopup="true"
              aria-expanded={fileOpen}
              onClick={() => {
                setFileOpen((v) => !v);
                setReportsOpen(false);
              }}
              onKeyDown={(e) => onDropdownButtonKeyDown(e, "file")}
            >
              File <span className="caret">▾</span>
            </button>

            <div
              ref={fileMenuRef}
              className={`menu ${fileOpen ? "show" : ""}`}
              role="menu"
              aria-label="File"
            >
              {can("users.manage") && (
                <button
                  role="menuitem"
                  onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                  onClick={() => handleTileSelect(() => go("users-manager"))}
                >
                  👥 User Management
                </button>
              )}

              {canBusiness() && (
                <button
                  role="menuitem"
                  onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                  onClick={() => handleTileSelect(() => go("business"))}
                >
                  🏢 Business Registration
                </button>
              )}

              {canRegisterStore() && (
                <button
                  role="menuitem"
                  onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                  onClick={() => handleTileSelect(() => openRegisterStore())}
                >
                  🏪 Register Store
                </button>
              )}

              {allowHotelModule && canHotelView() && (
                <>
                  <div className="menuSep" />
                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                    onClick={() => handleTileSelect(() => go("hotel-checkin"))}
                  >
                    🏨 Hotel Check-In / Out
                  </button>
                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                    onClick={() => handleTileSelect(() => go("hotel-rooms"))}
                  >
                    🚪 Rooms
                  </button>
                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                    onClick={() => handleTileSelect(() => go("hotel-guests"))}
                  >
                    🧑‍🤝‍🧑 Guests
                  </button>
                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                    onClick={() => handleTileSelect(() => go("hotel-stays"))}
                  >
                    🗓️ Stays
                  </button>
                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                    onClick={() => handleTileSelect(() => go("hotel-reports"))}
                  >
                    📊 Hotel Reports
                  </button>
                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                    onClick={() =>
                      handleTileSelect(() => go("hotel-supplies-pos"))
                    }
                  >
                    🛍️ Guest Supplies POS
                  </button>
                  <button
                    role="menuitem"
                    onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                    onClick={() =>
                      handleTileSelect(() => go("hotel-supplies-replenish"))
                    }
                  >
                    📦 Replenish Supplies
                  </button>
                </>
              )}

              <div className="menuSep" />

              <button
                role="menuitem"
                onKeyDown={(e) => onMenuItemKeyDown(e, "file")}
                onClick={doLogout}
                className="danger"
              >
                🚪 Logout
              </button>
            </div>
          </div>

          {!isHotel && !isRestaurant && (
            <div className="dropdown">
              <button
                ref={reportsBtnRef}
                className="navBtn"
                aria-haspopup="true"
                aria-expanded={reportsOpen}
                onClick={() => {
                  setReportsOpen((v) => !v);
                  setFileOpen(false);
                }}
                onKeyDown={(e) => onDropdownButtonKeyDown(e, "reports")}
              >
                Reports <span className="caret">▾</span>
              </button>

              <div
                ref={reportsMenuRef}
                className={`menu ${reportsOpen ? "show" : ""}`}
                role="menu"
                aria-label="Reports"
              >
                {can("reports.view") ? (
                  <>
                    <button
                      role="menuitem"
                      onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                      onClick={() => handleTileSelect(() => go("reports"))}
                    >
                      📈 Sales Report
                    </button>

                    <button
                      role="menuitem"
                      onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                      onClick={() => handleTileSelect(() => go("sales-summary"))}
                    >
                      👤📊 Sales Summary
                    </button>

                    <button
                      role="menuitem"
                      onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                      onClick={() =>
                        handleTileSelect(() => go("sale-items-report"))
                      }
                    >
                      🧾 Sales Items
                    </button>

                    <div className="menuSep" />

                    <button
                      role="menuitem"
                      onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                      onClick={() =>
                        handleTileSelect(() => go("replenishment-report"))
                      }
                    >
                      ➕ Replenishment
                    </button>

                    <button
                      role="menuitem"
                      onKeyDown={(e) => onMenuItemKeyDown(e, "reports")}
                      onClick={() => handleTileSelect(() => go("stock-report"))}
                    >
                      📦 Stock Report
                    </button>
                  </>
                ) : (
                  <div style={{ padding: 10, opacity: 0.85 }}>
                    Missing <code>reports.view</code>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="quickActions">
            {!isHotel && !isRestaurant && can("pos.sell") && isRetail && (
              <button
                className="pillBtn"
                onClick={() => handleTileSelect(() => go("sale"))}
              >
                🛒 New Sale
              </button>
            )}
            {!isHotel && !isRestaurant && can("pos.sell") && isBar && (
              <button
                className="pillBtn"
                onClick={() => handleTileSelect(() => go("pos"))}
              >
                🍹 POS (Bar)
              </button>
            )}
            {!isHotel && !isRestaurant && can("inventory.view") && (
              <button
                className="pillBtn"
                onClick={() => handleTileSelect(() => go("inventory"))}
              >
                📦 Inventory
              </button>
            )}
            {!isHotel && !isRestaurant && can("reports.view") && (
              <>
                <button
                  className="pillBtn"
                  onClick={() =>
                    handleTileSelect(() => go("replenishment-report"))
                  }
                >
                  ➕ Replenish
                </button>
                <button
                  className="pillBtn"
                  onClick={() => handleTileSelect(() => go("stock-report"))}
                >
                  📦🧾 Stock
                </button>
              </>
            )}
            {allowHotelModule && canHotelView() && (
              <>
                <button
                  className="pillBtn"
                  onClick={() => handleTileSelect(() => go("hotel-checkin"))}
                >
                  🏨 Hotel
                </button>
                <button
                  className="pillBtn"
                  onClick={() => handleTileSelect(() => go("hotel-reports"))}
                >
                  📊 Hotel Reports
                </button>
                <button
                  className="pillBtn"
                  onClick={() =>
                    handleTileSelect(() => go("hotel-supplies-pos"))
                  }
                >
                  🛍️ Supplies POS
                </button>
                <button
                  className="pillBtn"
                  onClick={() =>
                    handleTileSelect(() => go("hotel-supplies-replenish"))
                  }
                >
                  📦 Replenish Supplies
                </button>
              </>
            )}
          </div>
        </nav>

        <div className="rightMeta">
          <div
            className="userChip"
            title={username ? `Logged in as ${username}` : "Logged in"}
          >
            <span className="dot" />
            <span className="userText">{username || "User"}</span>
          </div>
        </div>
      </header>

      <main className="content">
        {!showTiles && (
          <div className="toolbar">
            <button className="primaryBtn" onClick={handleShowMenu}>
              ◀ Back to Menu
            </button>
            <div className="crumbs">
              <span className="crumbLabel">Page:</span>
              <span className="crumbValue">{page}</span>
            </div>
          </div>
        )}

        {showTiles && (
          <section className="tileGrid" aria-label="Quick actions">
            <div className="tileHeader">
              <div className="tileTitle">Dashboard</div>
              <div className="tileSubtitle">Choose an action to continue.</div>
            </div>

            {allowHotelModule && (
              <div className="notice">
                Hotel system available for this store, while common forms remain
                available.
              </div>
            )}

            <div className="tiles">
              {actions.map((a) => (
                <button
                  key={a.key}
                  className="tile"
                  onClick={() => handleTileSelect(a.onClick)}
                >
                  <div className="tileIcon" aria-hidden="true">
                    {a.icon}
                  </div>
                  <div className="tileText">
                    <div className="tileLabel">{a.label}</div>
                    <div className="tileHint">Open</div>
                  </div>
                  <div className="tileArrow" aria-hidden="true">
                    ›
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {!showTiles && (
          <section className="pageCard">
            {page === "home" && (
              <h1 className="pageTitle">Welcome to your POS Dashboard</h1>
            )}

            {page === "users-manager" &&
              (can("users.manage") ? (
                <UsersManager onBack={handleShowMenu} />
              ) : (
                <Forbidden need="users.manage" />
              ))}

            {page === "users-list" &&
              (can("users.manage") ? (
                <UsersList onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="users.manage" />
              ))}

            {page === "register-users" &&
              (can("users.manage") ? (
                <RegisterUsers onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="users.manage" />
              ))}

            {page === "business" &&
              (canBusiness() ? (
                <BusinessList onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="admin/super_user" />
              ))}

            {page === "register-store" &&
              (canRegisterStore() ? (
                <RegisterStore onSuccess={() => handleShowMenu()} />
              ) : (
                <Forbidden need="super_user" />
              ))}

            {page === "inventory" &&
              (!allowHotelModule && can("inventory.view") ? (
                <InventoryList onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="inventory.view" />
              ))}

            {page === "sale" &&
              isRetail &&
              !allowHotelModule &&
              (can("pos.sell") ? (
                <SalesWindow onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="pos.sell" />
              ))}

            {page === "reports" &&
              (!allowHotelModule && can("reports.view") ? (
                <Reports onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="reports.view" />
              ))}

            {page === "sales-summary" &&
              (!allowHotelModule && can("reports.view") ? (
                <SalesSummaryReport onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="reports.view" />
              ))}

            {page === "sale-items-report" &&
              (!allowHotelModule && can("reports.view") ? (
                <SaleItemsReport onBack={handleShowMenu} />
              ) : (
                <Forbidden need="reports.view" />
              ))}

            {page === "replenishment-report" &&
              (!allowHotelModule && can("reports.view") ? (
                <ReplenishmentReport onBack={handleShowMenu} />
              ) : (
                <Forbidden need="reports.view" />
              ))}

            {page === "stock-report" &&
              (!allowHotelModule && can("reports.view") ? (
                <StockReport onBack={handleShowMenu} />
              ) : (
                <Forbidden need="reports.view" />
              ))}

            {page === "replenish-report" &&
              (!allowHotelModule && can("reports.view") ? (
                <ReplenishReport onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="reports.view" />
              ))}

            {page === "pos" &&
              isBar &&
              !allowHotelModule &&
              (can("pos.sell") ? (
                <POSUI onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="pos.sell" />
              ))}

            {page === "hotel-checkin" &&
              (canHotelManage() ? (
                HotelCheckInScreen ? (
                  <HotelCheckInScreen onBack={handleShowMenu} api={api} />
                ) : (
                  <MissingHotel name="HotelCheckInScreen" />
                )
              ) : (
                <Forbidden need="hotel.manage" />
              ))}

            {page === "hotel-rooms" &&
              (canHotelManage() ? (
                <HotelRoomsScreen onBack={handleShowMenu} />
              ) : (
                <Forbidden need="hotel.manage" />
              ))}

            {page === "hotel-guests" &&
              (canHotelView() ? (
                HotelGuestsScreen ? (
                  <HotelGuestsScreen onBack={handleShowMenu} api={api} />
                ) : (
                  <MissingHotel name="HotelGuestsScreen" />
                )
              ) : (
                <Forbidden need="hotel.view" />
              ))}

            {page === "hotel-stays" &&
              (canHotelView() ? (
                HotelStaysScreen ? (
                  <HotelStaysScreen onBack={handleShowMenu} api={api} />
                ) : (
                  <MissingHotel name="HotelStaysScreen" />
                )
              ) : (
                <Forbidden need="hotel.view" />
              ))}

            {page === "hotel-reports" &&
              (canHotelView() ? (
                <HotelReportsScreen onBack={handleShowMenu} api={api} />
              ) : (
                <Forbidden need="hotel.view" />
              ))}

            {page === "hotel-supplies-pos" &&
              (canHotelManage() ? (
                <HotelSuppliesPOS
                  onBack={handleShowMenu}
                  onOpenReplenish={() => go("hotel-supplies-replenish")}
                  api={api}
                />
              ) : (
                <Forbidden need="hotel.manage" />
              ))}

            {page === "hotel-supplies-replenish" &&
              (canHotelManage() ? (
                <HotelSuppliesReplenish onBack={handleShowMenu} />
              ) : (
                <Forbidden need="hotel.manage" />
              ))}
          </section>
        )}
      </main>

      <style>{`
        :root{
          --bg:#f6f7fb;
          --surface:#ffffff;
          --stroke:rgba(15,23,42,.12);
          --text:#0f172a;
          --muted:#475569;
          --brand:#2563eb;
          --brand2:#1d4ed8;
          --good:#16a34a;
          --danger:#dc2626;
          --shadow: 0 18px 50px rgba(15,23,42,.10);
          --shadow2: 0 10px 28px rgba(15,23,42,.10);
          --radius:16px;
          --ring: 0 0 0 3px rgba(37,99,235,.22);
        }

        *{box-sizing:border-box}
        html, body { background: var(--bg) !important; color: var(--text) !important; }
        body{
          margin:0;
          background:
            radial-gradient(1000px 400px at 20% -10%, rgba(37,99,235,.10), transparent 55%),
            radial-gradient(900px 360px at 80% 0%, rgba(22,163,74,.06), transparent 60%),
            var(--bg) !important;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
        }

        .app-shell{ min-height:100vh; display:flex; flex-direction:column; }

        .topbar{
          position:sticky; top:0; z-index:50;
          background: rgba(255,255,255,.78);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--stroke);
          display:flex; align-items:center; gap:14px;
          padding: 10px 14px;
        }

        .brandWrap{ display:flex; align-items:center; gap:12px; min-width:220px; }
        .brandMark{
          width:40px; height:40px; border-radius:14px;
          background: linear-gradient(135deg, var(--brand), #60a5fa);
          color:white; font-weight:900; display:grid; place-items:center;
          box-shadow: 0 10px 25px rgba(37,99,235,.25);
        }
        .brandTitle{ font-weight:900; letter-spacing:.2px; }
        .brandSub{ font-size:12px; color:var(--muted); margin-top:2px; }

        .hamburger{ display:none; margin-left:auto; }

        .iconBtn{
          border:1px solid var(--stroke);
          background: var(--surface);
          border-radius: 12px;
          padding: 10px 12px;
          cursor:pointer;
        }
        .iconBtn:focus{ outline:none; box-shadow: var(--ring); }

        .nav{ display:flex; align-items:center; gap:10px; flex:1; }
        .nav.open{ display:flex; }

        .navBtn{
          border:1px solid var(--stroke);
          background: var(--surface);
          border-radius: 12px;
          padding: 10px 12px;
          cursor:pointer;
          display:flex; align-items:center; gap:8px;
        }
        .navBtn:focus{ outline:none; box-shadow: var(--ring); }
        .caret{ opacity:.7; }

        .dropdown{ position:relative; }
        .menu{
          position:absolute; top:44px; left:0;
          min-width:240px;
          background: var(--surface);
          border:1px solid var(--stroke);
          border-radius: 14px;
          box-shadow: var(--shadow2);
          padding: 8px;
          display:none;
        }
        .menu.show{ display:block; }
        .menu button{
          width:100%;
          text-align:left;
          border:1px solid transparent;
          background:transparent;
          padding:10px 10px;
          border-radius:12px;
          cursor:pointer;
        }
        .menu button:hover{ background:#f3f4f6; }
        .menu button:focus{ outline:none; box-shadow: var(--ring); }
        .menuSep{ height:1px; background: var(--stroke); margin: 8px 4px; }
        .danger{ color: var(--danger); }

        .quickActions{ display:flex; gap:8px; margin-left:10px; flex-wrap:wrap; }
        .pillBtn{
          border:1px solid var(--stroke);
          background: var(--surface);
          border-radius: 999px;
          padding: 9px 12px;
          cursor:pointer;
          font-weight:700;
        }
        .pillBtn:hover{ background:#f8fafc; }

        .rightMeta{ margin-left:auto; display:flex; align-items:center; gap:10px; }
        .userChip{
          display:flex; align-items:center; gap:10px;
          border:1px solid var(--stroke);
          background: var(--surface);
          border-radius: 999px;
          padding: 8px 12px;
        }
        .dot{ width:10px; height:10px; border-radius:999px; background: var(--good); box-shadow: 0 0 0 3px rgba(22,163,74,.15); }
        .userText{ font-weight:800; font-size:13px; }

        .content{ flex:1; padding: 14px; }
        .toolbar{
          display:flex; align-items:center; justify-content:space-between;
          margin: 6px 0 12px;
          padding: 10px 12px;
          border: 1px solid var(--stroke);
          border-radius: var(--radius);
          background: rgba(255,255,255,.7);
        }
        .crumbs{ display:flex; align-items:center; gap:8px; color: var(--muted); font-weight:700; }
        .crumbLabel{ opacity:.8; }
        .crumbValue{ color: var(--text); }

        .primaryBtn{
          border: 1px solid rgba(37,99,235,.25);
          background: linear-gradient(135deg, var(--brand), var(--brand2));
          color: white;
          padding: 10px 14px;
          border-radius: 12px;
          cursor:pointer;
          font-weight:900;
        }
        .primaryBtn:hover{ filter: brightness(0.98); }

        .tileGrid{
          border: 1px solid var(--stroke);
          background: rgba(255,255,255,.65);
          border-radius: 22px;
          box-shadow: var(--shadow);
          padding: 18px;
        }
        .tileHeader{ margin-bottom:14px; }
        .tileTitle{ font-size: 26px; font-weight: 1000; }
        .tileSubtitle{ color: var(--muted); margin-top:4px; }

        .tiles{
          display:grid;
          grid-template-columns: repeat(3, minmax(260px, 1fr));
          gap: 12px;
        }

        .tile{
          display:flex; align-items:center; justify-content:space-between;
          gap: 12px;
          border:1px solid var(--stroke);
          background: var(--surface);
          border-radius: 18px;
          padding: 14px;
          cursor:pointer;
          box-shadow: 0 10px 28px rgba(15,23,42,.08);
        }
        .tile:hover{ transform: translateY(-1px); box-shadow: 0 14px 32px rgba(15,23,42,.10); }
        .tileIcon{
          width:46px; height:46px; border-radius: 16px;
          background: #f1f5f9;
          display:grid; place-items:center;
          font-size: 20px;
        }
        .tileText{ text-align:left; flex:1; }
        .tileLabel{ font-weight: 1000; font-size: 16px; }
        .tileHint{ color: var(--muted); margin-top:3px; font-weight:700; font-size: 13px; }
        .tileArrow{ font-size: 22px; opacity:.6; font-weight: 1000; }

        .pageCard{
          border: 1px solid var(--stroke);
          background: rgba(255,255,255,.75);
          border-radius: 22px;
          box-shadow: var(--shadow);
          padding: 18px;
        }
        .pageTitle{ font-size: 22px; font-weight: 1000; margin: 0; }

        .notice{
          border:1px dashed rgba(37,99,235,.35);
          background: rgba(37,99,235,.08);
          padding: 12px;
          border-radius: 14px;
          margin: 10px 0;
          font-weight: 800;
        }

        @media (max-width: 1100px){
          .tiles{ grid-template-columns: repeat(2, minmax(260px, 1fr)); }
        }
        @media (max-width: 760px){
          .hamburger{ display:block; }
          .nav{ display:none; flex-direction:column; align-items:stretch; padding: 10px 0; }
          .nav.open{ display:flex; }
          .rightMeta{ margin-left: 0; }
          .tiles{ grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}